import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUsers,
    faHandHoldingHeart,
    faChartLine,
    faCalendarAlt,
    faDollarSign,
    faExclamationCircle,
    faPlus,
    faList
} from '@fortawesome/free-solid-svg-icons';
import { DonationService, CampaignService } from '../../../Services/Axios/Api';
import './HomeAdminPanel.css';
import { useNavigate } from 'react-router-dom';

const HomeAdminPanel = () => {
    const [stats, setStats] = useState({
        totalDonors: 0,
        totalDonations: 0,
        totalAmount: 0,
        activeCampaigns: 0,
        currency: 'EGP',
        paidDonations: 0
    });
    const navigate  = useNavigate();
    const [recentDonations, setRecentDonations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                // Fetch donors
                const donorsResponse = await DonationService.getDonors();
                const totalDonors = donorsResponse.result.length;

                // Fetch active campaigns
                const campaignsResponse = await CampaignService.getCampaigns();

                const activeCampaigns = campaignsResponse.result.length;

                // Fetch recent donations
                const donationsResponse = await DonationService.getDonations();
                const donations = donationsResponse.result;

                // Calculate total donations and amount
                const totalDonations = donations.length;
                const paidDonations = donations.filter(donation => donation.paymentStatus === 'Completed');
                const totalAmount = paidDonations.reduce((sum, donation) => sum + donation.amount, 0);
                const currency = donations[0]?.currency || 'EGP'; // Get currency from first donation or default to EGP

                // Get recent donations (last 5)
                const recent = donations
                    .sort((a, b) => new Date(b.donationDate) - new Date(a.donationDate))
                    .slice(0, 5);

                setStats({
                    totalDonors,
                    totalDonations,
                    totalAmount,
                    activeCampaigns,
                    currency,
                    paidDonations: paidDonations.length
                });
                setRecentDonations(recent);
            } catch (err) {
                setError('Failed to fetch dashboard data');
                console.error('Error fetching dashboard data:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchDashboardData();
    }, []);

    if (loading) {
        return (
            <div className="AdminPanel">
                <div className="dashboard-loading">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="AdminPanel">
                <div className="dashboard-error">
                    <FontAwesomeIcon icon={faExclamationCircle} className="error-icon" />
                    <p>{error}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="AdminPanel">
            <Container fluid className="dashboard-container">
                <div className="dashboard-header">
                    <h1>Dashboard Overview</h1>
                    <p className="dashboard-subtitle">Welcome to your charity management dashboard</p>
                </div>

                <Row className="stats-cards">
                    <Col md={6} lg={3}>
                        <Card className="stat-card donors-card">
                            <Card.Body>
                                <div className="stat-icon">
                                    <FontAwesomeIcon icon={faUsers} />
                                </div>
                                <div className="stat-content">
                                    <h3>{stats.totalDonors}</h3>
                                    <p>Total Donors</p>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col md={6} lg={3}>
                        <Card className="stat-card donations-card">
                            <Card.Body>
                                <div className="stat-icon">
                                    <FontAwesomeIcon icon={faHandHoldingHeart} />
                                </div>
                                <div className="stat-content">
                                    <h3>{stats.totalDonations}</h3>
                                    <p>Total Donations</p>
                                    <small className="text-muted">{stats.paidDonations} paid</small>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col md={6} lg={3}>
                        <Card className="stat-card amount-card">
                            <Card.Body>
                                <div className="stat-icon">
                                    <FontAwesomeIcon icon={faDollarSign} />
                                </div>
                                <div className="stat-content">
                                    <h3>{stats.currency} {stats.totalAmount.toLocaleString()}</h3>
                                    <p>Total Amount</p>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col md={6} lg={3}>
                        <Card className="stat-card campaigns-card">
                            <Card.Body>
                                <div className="stat-icon">
                                    <FontAwesomeIcon icon={faChartLine} />
                                </div>
                                <div className="stat-content">
                                    <h3>{stats.activeCampaigns}</h3>
                                    <p>Active Campaigns</p>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                <Row className="dashboard-content">
                    <Col lg={8}>
                        <Card className="recent-donations-card">
                            <Card.Header>
                                <h5>
                                    <FontAwesomeIcon icon={faChartLine} className="me-2" />
                                    Recent Donations
                                </h5>
                            </Card.Header>
                            <Card.Body>
                                <div className="recent-donations-list">
                                    {recentDonations.map((donation, index) => (
                                        <div key={index} className="donation-item">
                                            <div className="donation-info">
                                                <h6>{donation.campaignName}</h6>
                                                <p className="donor-email">{donation.donorEmail}</p>
                                            </div>
                                            <div className="donation-details">
                                                <span className="amount">{donation.currency} {donation.amount}</span>
                                                <span className="date">
                                                    <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
                                                    {new Date(donation.donationDate).toLocaleDateString()}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col lg={4}>
                        <Card className="quick-actions-card">
                            <Card.Header>
                                <h5>Quick Actions</h5>
                            </Card.Header>
                            <Card.Body>
                                <div className="quick-actions-list">
                                    <button className="action-button" onClick={()=>navigate('/bj-admin/donations/campaigns/add')}>
                                        <FontAwesomeIcon icon={faPlus} />
                                        Create New Campaign
                                    </button>
                                    <button className="action-button" onClick={()=>navigate('/bj-admin/donations/donors')}>
                                        <FontAwesomeIcon icon={faUsers} />
                                        View All Donors
                                    </button>
                                    <button className="action-button" onClick={()=>navigate('/bj-admin/donations')}>
                                        <FontAwesomeIcon icon={faList} />
                                        View Donations
                                    </button>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default HomeAdminPanel;
